import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, Download, ChevronUp, ChevronDown, TrendingUp } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { dashboardService, CompletedBacktestSummary } from '../../../services/DashboardService';
import { BacktestResultsChart } from '../../../components/library/backtest-analytics/BacktestResultsChart';
import { BacktestSeriesPayload } from '../../../components/utils/returns';
import { BacktestRequest } from '../../order-ticket/strategy-backtest/config/types';
import { downloadBacktestAsExcel } from '../../../components/utils/backtestExport';
import { StrategyConfigView } from '../../order-ticket/strategy-backtest/components/StrategyConfigView';
import { SymbolLogo } from '../../../components/common/SymbolLogo';
import { useOrderTicketNavigation } from '../../order-ticket/navigation/OrderTicketNavigationContext';
import {
  calculatePositionRowMetrics,
  calculateStrategyLegPnl,
  calculatePortfolioTotalValue,
  formatCurrencyValue,
  isActiveStrategyLeg,
  normalizeSymbol,
  resolvePositionMarkPrice,
  resolveStrategyLegMarkPrice,
} from '../../../components/trading-dashboard/portfolioValue';
import type { WidgetInstanceProps } from '../../../components/grid-dashboard';
import styles from './PortfolioWidget.module.css';

import { usePositionState } from '../shared/usePositionState';
import { useGroupedPositions } from '../shared/useGroupedPositions';
import { useStrategyActions } from '../shared/useStrategyActions';
import { useStrategyLegEntryBasis } from '../shared/useStrategyLegEntryBasis';
import { WidgetErrorBoundary } from '../shared/WidgetErrorBoundary';
import { StrategyCloseModal, StrategyViewModal } from '../shared/StrategyModals';
import {
  fmtPrice, fmtCurrency, fmtReturnPct, fmtTime, fmtDate, fmtPnl, fmtPct,
  toFiniteNumber, pnlStyle, errorMessage, toBacktestRequest,
} from '../shared/formatters';
import { StrategyPositionSnapshot } from '../../../types/trading';

/* ── Backtest sort helpers ────────────────────────────────────────── */
type SortField = 'backtest_name' | 'start_date' | 'end_date' | 'total_pnl';
type SortDir = 'asc' | 'desc';

function comparator(a: CompletedBacktestSummary, b: CompletedBacktestSummary, field: SortField, dir: SortDir): number {
  let cmp = 0;
  if (field === 'total_pnl') cmp = a.total_pnl - b.total_pnl;
  else cmp = (a[field] ?? '').localeCompare(b[field] ?? '');
  return dir === 'asc' ? cmp : -cmp;
}

/* ── StatsPane ────────────────────────────────────────────────────── */
const StatsPane: React.FC<{ stats: BacktestSeriesPayload | null; onDownload: () => void; hasData: boolean }> = ({ stats, onDownload, hasData }) => {
  if (!stats) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', color: 'var(--config-text-muted, #888)', gap: '8px' }}>
      <BarChart3 size={28} style={{ opacity: 0.25 }} />
      <span>No statistics available</span>
    </div>
  );
  const rows: { label: string; value: string; color: string }[] = [
    { label: 'Net Capital Gain', value: fmtPnl(stats.net_capital_gain), color: stats.net_capital_gain >= 0 ? '#22c55e' : '#ef4444' },
    { label: 'Total Trades', value: String(stats.total_trades), color: 'var(--config-text-secondary, #ccc)' },
    { label: 'Avg PnL / Trade', value: fmtPnl(stats.average_pnl_per_trade), color: stats.average_pnl_per_trade >= 0 ? '#22c55e' : '#ef4444' },
    { label: 'Hit Ratio', value: fmtPct(stats.hit_ratio), color: 'var(--config-text-secondary, #ccc)' },
    { label: 'Max Gain', value: fmtPnl(stats.max_gain), color: '#22c55e' },
    { label: 'Max Loss', value: fmtPnl(Math.abs(stats.max_loss)), color: '#ef4444' },
    { label: 'Max Drawdown', value: fmtPnl(Math.abs(stats.max_drawdown)), color: '#ef4444' },
    { label: 'Avg Cost / Trade', value: fmtPnl(Math.abs(stats.average_cost_per_trade)), color: 'var(--config-text-secondary, #ccc)' },
    { label: 'Max Cost', value: fmtPnl(Math.abs(stats.max_cost)), color: 'var(--config-text-secondary, #ccc)' },
    { label: 'Avg Profit %', value: fmtPct(stats.average_profit_percent), color: stats.average_profit_percent >= 0 ? '#22c55e' : '#ef4444' },
    { label: 'Max Profit %', value: fmtPct(stats.max_profit_percent), color: '#22c55e' },
    { label: 'Max Loss %', value: fmtPct(Math.abs(stats.max_loss_percent)), color: '#ef4444' },
  ];
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        {rows.map((r) => (
          <div key={r.label} style={{ background: 'var(--config-bg-tertiary, #1a1a2e)', border: '1px solid var(--config-border-muted, #333)', borderRadius: '8px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '0.6875rem', fontWeight: 500, color: 'var(--config-text-muted, #888)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{r.label}</span>
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: r.color }}>{r.value}</span>
          </div>
        ))}
      </div>
      {hasData && (
        <button type="button" onClick={onDownload} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', marginTop: '10px', background: 'var(--config-accent-primary, #3b82f6)', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
          <Download size={14} /> Download Excel
        </button>
      )}
    </>
  );
};

/* ── Inner component ──────────────────────────────────────────────── */
const PortfolioInner: React.FC = () => {
  const { userUuid, getToken, isAuthenticated } = useAuth();
  const { ticksBySymbol, openOrderTicket } = useOrderTicketNavigation();

  /* ── Position state from shared hook ── */
  const posState = usePositionState();
  const {
    cash, positions, strategyPositions,
    loading: positionsLoading, error: positionsError,
    latestCloseBySymbol, prevCloseBySymbol, effectiveMarketIsOpen,
  } = posState;

  /* ── Local strategy positions for optimistic close ── */
  const [localStrategyPositions, setLocalStrategyPositions] = useState<StrategyPositionSnapshot[] | null>(null);
  const effectiveStrategyPositions = localStrategyPositions ?? strategyPositions;
  useEffect(() => { setLocalStrategyPositions(null); }, [strategyPositions]);

  const strategyActions = useStrategyActions(
    ((updater: React.SetStateAction<StrategyPositionSnapshot[]>) => {
      setLocalStrategyPositions((prev) => {
        const current = prev ?? strategyPositions;
        return typeof updater === 'function' ? updater(current) : updater;
      });
    }) as React.Dispatch<React.SetStateAction<StrategyPositionSnapshot[]>>,
  );

  /* ── Grouped positions + strategy leg basis ── */
  const groupedPositions = useGroupedPositions(positions);
  const basisRef = useStrategyLegEntryBasis(effectiveStrategyPositions);

  const [expandedPositionSymbols, setExpandedPositionSymbols] = useState<Record<string, boolean>>({});
  const togglePositionGroup = useCallback(
    (symbol: string) => setExpandedPositionSymbols((prev) => ({ ...prev, [symbol]: !prev[symbol] })),
    [],
  );

  const totalPortfolioValue = useMemo(
    () => calculatePortfolioTotalValue(
      cash, positions, ticksBySymbol, strategyPositions, latestCloseBySymbol, effectiveMarketIsOpen,
    ),
    [cash, positions, ticksBySymbol, strategyPositions, latestCloseBySymbol, effectiveMarketIsOpen],
  );

  /* ── Backtest state (portfolio-specific) ── */
  const [backtests, setBacktests] = useState<CompletedBacktestSummary[]>([]);
  const [backtestLoading, setBacktestLoading] = useState(true);
  const [backtestError, setBacktestError] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resultsData, setResultsData] = useState<BacktestSeriesPayload | null>(null);
  const [configData, setConfigData] = useState<BacktestRequest | null>(null);
  const [leftTab, setLeftTab] = useState<'stats' | 'config'>('stats');
  const [sortField, setSortField] = useState<SortField>('backtest_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  /* ── Load backtests on mount ── */
  useEffect(() => {
    if (!isAuthenticated || !userUuid) return;
    let cancelled = false;
    const load = async () => {
      setBacktestLoading(true); setBacktestError(null);
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const result = await dashboardService.listBacktests(userUuid, token);
        if (!cancelled) setBacktests(result.backtests);
      } catch {
        if (!cancelled) setBacktestError('Failed to load backtests');
      } finally {
        if (!cancelled) setBacktestLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isAuthenticated, userUuid, getToken]);

  /* ── Load backtest detail ── */
  const loadDetail = useCallback(async (name: string) => {
    if (!userUuid) return;
    setDetailLoading(true); setResultsData(null); setConfigData(null);
    try {
      const token = await getToken();
      if (!token) return;
      const [rResp, cResp] = await Promise.all([
        dashboardService.getBacktestResultsByName(userUuid, token, name),
        dashboardService.getBacktestConfigByName(userUuid, token, name),
      ]);
      if (rResp.results) setResultsData(rResp.results as BacktestSeriesPayload);
      if (cResp.config_data) setConfigData(cResp.config_data as BacktestRequest);
    } catch (e) { console.error('Failed to load backtest detail:', e); }
    finally { setDetailLoading(false); }
  }, [userUuid, getToken]);

  const handleSelect = useCallback((name: string) => {
    if (name === selectedName) { setSelectedName(null); setResultsData(null); setConfigData(null); }
    else { setSelectedName(name); loadDetail(name); }
  }, [selectedName, loadDetail]);

  const sorted = useMemo(() => [...backtests].sort((a, b) => comparator(a, b, sortField, sortDir)), [backtests, sortField, sortDir]);
  const toggleSort = (field: SortField) => {
    if (field === sortField) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const pnlStats = useMemo(() => resultsData ?? null, [resultsData]);
  const handleDownload = useCallback(() => { if (resultsData?.raw_data) downloadBacktestAsExcel(resultsData.raw_data); }, [resultsData]);

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    const isActive = sortField === field;
    const Icon = isActive && sortDir === 'desc' ? ChevronDown : ChevronUp;
    return <Icon size={12} style={{ marginLeft: '4px', opacity: isActive ? 1 : 0.5, color: isActive ? 'var(--config-accent-primary, #3b82f6)' : undefined, verticalAlign: 'middle' }} />;
  };

  /* ── Shared inline style tokens ── */
  const thS: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--config-text-muted, #888)', background: 'var(--config-bg-secondary, #111)', borderBottom: '1px solid var(--config-border-muted, #333)', whiteSpace: 'nowrap', cursor: 'default' };
  const tdS: React.CSSProperties = { padding: '8px 12px', fontSize: '0.8125rem', color: 'var(--config-text-secondary, #ccc)', borderBottom: '1px solid var(--config-border-muted, #333)' };
  const symbolCellS: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--config-text-primary, #e2e8f0)', fontWeight: 600 };
  const pnlPos: React.CSSProperties = { color: '#22c55e', fontWeight: 600 };
  const pnlNeg: React.CSSProperties = { color: '#ef4444', fontWeight: 600 };
  const btnS: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '4px 8px',
    border: '1px solid var(--pw-action-border, var(--config-border-muted, #333))',
    borderRadius: '6px',
    color: 'var(--pw-action-color, var(--config-text-secondary, #ccc))',
    background: 'var(--pw-action-bg, transparent)',
    fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
    transition: 'color 140ms ease, border-color 140ms ease, background-color 140ms ease',
  };
  const headerS: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--config-bg-secondary, #111)', borderBottom: '1px solid var(--config-border-muted, #333)', fontSize: '0.8125rem', fontWeight: 700 };
  const accentHeaderS: React.CSSProperties = { ...headerS, background: 'rgba(255,255,255,0.04)', borderBottomColor: 'rgba(255,255,255,0.08)', color: 'var(--config-text-primary, #e2e8f0)' };
  const spinnerS: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', flex: 1 };
  const errorS: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', color: 'var(--config-error, #ef4444)', fontSize: '0.875rem', flex: 1 };
  const emptyS: React.CSSProperties = { textAlign: 'center', color: 'var(--config-text-muted, #888)', padding: '16px' };
  const tableS: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' };
  const wrapS: React.CSSProperties = { flex: 1, overflowY: 'auto', minHeight: 0 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', color: 'var(--config-text-primary, #e2e8f0)' }}>
      {/* Detail View */}
      {selectedName && (
        <div style={{ display: 'flex', flex: 1, minHeight: 0, borderBottom: '1px solid var(--config-border-muted, #333)' }}>
          <div style={{ width: '340px', minWidth: '280px', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--config-border-muted, #333)', overflowY: 'auto' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid var(--config-border-muted, #333)', background: 'var(--config-bg-secondary, #111)', position: 'sticky', top: 0, zIndex: 5 }}>
              <button type="button" onClick={() => setLeftTab('stats')} style={{ flex: 1, padding: '8px 0', textAlign: 'center', fontSize: '0.8125rem', fontWeight: 600, color: leftTab === 'stats' ? 'var(--config-accent-primary, #3b82f6)' : 'var(--config-text-muted, #888)', background: 'none', border: 'none', cursor: 'pointer', borderBottom: leftTab === 'stats' ? '2px solid var(--config-accent-primary, #3b82f6)' : '2px solid transparent' }}>Statistics</button>
              <button type="button" onClick={() => setLeftTab('config')} style={{ flex: 1, padding: '8px 0', textAlign: 'center', fontSize: '0.8125rem', fontWeight: 600, color: leftTab === 'config' ? 'var(--config-accent-primary, #3b82f6)' : 'var(--config-text-muted, #888)', background: 'none', border: 'none', cursor: 'pointer', borderBottom: leftTab === 'config' ? '2px solid var(--config-accent-primary, #3b82f6)' : '2px solid transparent' }}>Configuration</button>
            </div>
            <div style={{ flex: 1, padding: '12px', overflowY: 'auto' }}>
              {detailLoading ? <div style={spinnerS}>Loading…</div> : leftTab === 'stats' ? <StatsPane stats={pnlStats} onDownload={handleDownload} hasData={!!resultsData?.raw_data} /> : <StrategyConfigView config={configData} />}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {detailLoading ? <div style={spinnerS}>Loading…</div> : resultsData ? (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}><BacktestResultsChart data={resultsData} height="100%" minimal /></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--config-text-muted, #888)', gap: '8px' }}>
                <TrendingUp size={36} style={{ opacity: 0.25 }} /><span>No results available</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom sections */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
        {/* Backtest Table */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '120px' }}>
          <div style={headerS}>
            <span>Saved Backtests{!backtestLoading && <span style={{ fontSize: '0.75rem', color: 'var(--config-text-muted, #888)', marginLeft: '6px' }}>({backtests.length})</span>}</span>
          </div>
          {backtestLoading ? <div style={spinnerS}>Loading…</div> : backtestError ? <div style={errorS}>{backtestError}</div> : backtests.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', color: 'var(--config-text-muted, #888)', gap: '8px' }}>
              <BarChart3 size={36} style={{ opacity: 0.3 }} />
              <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>No backtests yet</span>
              <span style={{ fontSize: '0.8125rem' }}>Run a backtest from the chat and save it to see it here.</span>
            </div>
          ) : (
            <div style={wrapS}>
              <table style={tableS}>
                <thead><tr>
                  <th style={{ ...thS, cursor: 'pointer' }} onClick={() => toggleSort('backtest_name')}>Name <SortIcon field="backtest_name" /></th>
                  <th style={{ ...thS, cursor: 'pointer' }} onClick={() => toggleSort('start_date')}>Start Date <SortIcon field="start_date" /></th>
                  <th style={{ ...thS, cursor: 'pointer' }} onClick={() => toggleSort('end_date')}>End Date <SortIcon field="end_date" /></th>
                  <th style={{ ...thS, cursor: 'pointer' }} onClick={() => toggleSort('total_pnl')}>Total PnL <SortIcon field="total_pnl" /></th>
                </tr></thead>
                <tbody>
                  {sorted.map((bt) => (
                    <tr key={bt.backtest_name} style={{ cursor: 'pointer', ...(bt.backtest_name === selectedName ? { background: 'rgba(59,130,246,0.12)' } : {}) }} onClick={() => handleSelect(bt.backtest_name)}>
                      <td style={tdS}><span style={{ color: 'var(--config-accent-primary, #3b82f6)', fontWeight: 600 }}>{bt.backtest_name}</span></td>
                      <td style={tdS}>{fmtDate(bt.start_date)}</td>
                      <td style={tdS}>{fmtDate(bt.end_date)}</td>
                      <td style={{ ...tdS, ...(bt.total_pnl >= 0 ? pnlPos : pnlNeg) }}>{fmtPnl(bt.total_pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Positions Table */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '120px' }}>
          <div style={accentHeaderS}>
            <span>
              Positions
              {!positionsLoading && (
                <span style={{ fontSize: '0.75rem', color: 'var(--config-text-muted, #888)', marginLeft: '6px' }}>
                  ({groupedPositions.length})
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: '16px', marginLeft: 'auto' }}>
              <div><span style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', opacity: 0.8 }}>Total Portfolio Value </span><span style={{ fontSize: '0.8125rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{positionsLoading || positionsError ? '--' : formatCurrencyValue(totalPortfolioValue)}</span></div>
              <div><span style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', opacity: 0.8 }}>Cash </span><span style={{ fontSize: '0.8125rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{positionsLoading || positionsError ? '--' : formatCurrencyValue(cash)}</span></div>
            </div>
          </div>
          {positionsLoading ? <div style={spinnerS}>Loading…</div> : positionsError ? <div style={errorS}>{positionsError}</div> : (
            <div style={wrapS}>
              <table style={tableS}>
                <thead><tr>
                  <th style={thS}>Symbol</th><th style={thS}>Net Qty</th><th style={thS}>Avg Cost Basis</th><th style={thS}>Last</th><th style={thS}>Previous</th><th style={thS}>Total Cost</th><th style={thS}>PnL</th><th style={thS}>% Daily Return</th><th style={thS}>% Total Return</th><th style={thS}>Trade At</th><th style={thS}>Updated At</th><th style={thS}>Action</th>
                </tr></thead>
                <tbody>
                  {groupedPositions.length === 0 && <tr><td colSpan={12} style={emptyS}>No open positions</td></tr>}
                  {groupedPositions.map((group) => {
                    const pos = group.aggregate;
                    const sym = group.symbol;
                    const closeQty = Math.abs(pos.net_qty);
                    const hasChildren = group.lots.length > 1;
                    const expanded = !!expandedPositionSymbols[sym];
                    const lp = resolvePositionMarkPrice(pos, ticksBySymbol, latestCloseBySymbol, effectiveMarketIsOpen);
                    const prevCloseValue = prevCloseBySymbol[sym];
                    const previousClose = prevCloseValue?.status === 'ok' ? prevCloseValue.prev_close : null;
                    const metrics = calculatePositionRowMetrics({
                      position: pos, resolvedLast: lp, previousClose,
                      marketIsOpen: effectiveMarketIsOpen, forceLastEqualsPreviousWhenClosed: true,
                    });

                    return (
                      <React.Fragment key={sym}>
                        <tr
                          style={hasChildren ? { cursor: 'pointer', background: 'rgba(255,255,255,0.04)' } : undefined}
                          onClick={hasChildren ? () => togglePositionGroup(sym) : undefined}
                        >
                          <td style={tdS}>
                            <span style={symbolCellS}>
                              {hasChildren && <span style={{ width: '14px', textAlign: 'center' }}>{expanded ? '▼' : '▶'}</span>}
                              <SymbolLogo symbol={sym} size={16} />
                              <span>{sym}</span>
                              {hasChildren && (
                                <span style={{ fontSize: '0.72rem', color: 'var(--config-text-muted, #888)' }}>
                                  {group.lots.length} lots
                                </span>
                              )}
                            </span>
                          </td>
                          <td style={tdS}>{pos.net_qty}</td>
                          <td style={tdS}>{fmtPrice(pos.avg_price)}</td>
                          <td style={tdS}>{fmtPrice(metrics.last)}</td>
                          <td style={tdS}>{fmtPrice(metrics.previous)}</td>
                          <td style={tdS}>{fmtCurrency(metrics.totalCost)}</td>
                          <td style={{ ...tdS, ...pnlStyle(metrics.pnl) }}>{fmtCurrency(metrics.pnl)}</td>
                          <td style={{ ...tdS, ...pnlStyle(metrics.dailyReturnPct) }}>{fmtReturnPct(metrics.dailyReturnPct)}</td>
                          <td style={{ ...tdS, ...pnlStyle(metrics.totalReturnPct) }}>{fmtReturnPct(metrics.totalReturnPct)}</td>
                          <td style={tdS}>{fmtTime(pos.trade_at)}</td>
                          <td style={tdS}>{fmtTime(pos.updated_at)}</td>
                          <td style={tdS}>
                            <button
                              type="button"
                              className={styles.actionButton}
                              style={btnS}
                              disabled={closeQty <= 0}
                              onClick={(event) => {
                                event.stopPropagation();
                                openOrderTicket({ prefill: { symbol: sym, side: pos.net_qty > 0 ? 'sell' : 'buy', quantity: closeQty } });
                              }}
                              title="Close this position"
                              aria-label={`Close ${sym} position`}
                            >
                              Close
                            </button>
                          </td>
                        </tr>
                        {hasChildren && expanded && group.lots.map((lot) => {
                          const lotCloseQty = Math.abs(lot.net_qty);
                          const lotLast = resolvePositionMarkPrice(lot, ticksBySymbol, latestCloseBySymbol, effectiveMarketIsOpen);
                          const lotMetrics = calculatePositionRowMetrics({
                            position: lot, resolvedLast: lotLast, previousClose,
                            marketIsOpen: effectiveMarketIsOpen, forceLastEqualsPreviousWhenClosed: true,
                          });
                          return (
                            <tr key={lot.lot_id || `${lot.user_uuid}-${lot.symbol}-${lot.trade_at}`} style={{ background: 'rgba(255,255,255,0.01)' }}>
                              <td style={tdS}>
                                <span style={{ ...symbolCellS, paddingLeft: '26px', fontWeight: 500 }}>
                                  <SymbolLogo symbol={sym} size={14} />
                                  <span>{sym}</span>
                                </span>
                              </td>
                              <td style={tdS}>{lot.net_qty}</td>
                              <td style={tdS}>{fmtPrice(lot.avg_price)}</td>
                              <td style={tdS}>{fmtPrice(lotMetrics.last)}</td>
                              <td style={tdS}>{fmtPrice(lotMetrics.previous)}</td>
                              <td style={tdS}>{fmtCurrency(lotMetrics.totalCost)}</td>
                              <td style={{ ...tdS, ...pnlStyle(lotMetrics.pnl) }}>{fmtCurrency(lotMetrics.pnl)}</td>
                              <td style={{ ...tdS, ...pnlStyle(lotMetrics.dailyReturnPct) }}>{fmtReturnPct(lotMetrics.dailyReturnPct)}</td>
                              <td style={{ ...tdS, ...pnlStyle(lotMetrics.totalReturnPct) }}>{fmtReturnPct(lotMetrics.totalReturnPct)}</td>
                              <td style={tdS}>{fmtTime(lot.trade_at)}</td>
                              <td style={tdS}>{fmtTime(lot.updated_at)}</td>
                              <td style={tdS}>
                                <button
                                  type="button"
                                  className={styles.actionButton}
                                  style={btnS}
                                  disabled={lotCloseQty <= 0}
                                  onClick={() => openOrderTicket({ prefill: { symbol: sym, side: lot.net_qty > 0 ? 'sell' : 'buy', quantity: lotCloseQty } })}
                                  title="Close this lot"
                                  aria-label={`Close ${sym} lot`}
                                >
                                  Close
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Strategy Positions Table */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '120px' }}>
          <div style={accentHeaderS}>
            <span>Strategy Positions{!positionsLoading && <span style={{ fontSize: '0.75rem', color: 'var(--config-text-muted, #888)', marginLeft: '6px' }}>({effectiveStrategyPositions.length})</span>}</span>
          </div>
          {positionsLoading ? <div style={spinnerS}>Loading…</div> : positionsError ? <div style={errorS}>{positionsError}</div> : (
            <div style={wrapS}>
              <table style={tableS}>
                <thead><tr>
                  <th style={thS}>Leg Id</th><th style={thS}>Symbol</th><th style={thS}>Net Qty</th><th style={thS}>Avg Cost Basis</th><th style={thS}>Last Price</th><th style={thS}>PnL</th><th style={thS}>Trade At</th><th style={thS}>Updated At</th><th style={thS}>Action</th>
                </tr></thead>
                <tbody>
                  {effectiveStrategyPositions.length === 0 && <tr><td colSpan={9} style={emptyS}>No strategy positions</td></tr>}
                  {effectiveStrategyPositions.map((sp) => {
                    const isExp = !!strategyActions.expandedStrategies[sp.strategy_position_id];
                    const visLegs = sp.legs.filter(isActiveStrategyLeg);
                    return (
                      <React.Fragment key={sp.strategy_position_id}>
                        <tr style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }} onClick={() => strategyActions.toggleStrategyExpanded(sp.strategy_position_id)}>
                          <td colSpan={9} style={{ ...tdS, borderBottomColor: 'rgba(255,255,255,0.08)', color: 'var(--config-text-secondary, #ccc)', fontWeight: 600 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <span style={{ display: 'inline-block', width: '24px', textAlign: 'center' }}>{isExp ? '▼' : '▶'}</span>
                                <span style={{ marginRight: '12px' }}>{sp.name}</span>
                                <span style={{ marginRight: '12px', fontSize: '0.74rem', color: 'var(--config-text-muted, #888)' }}>Legs: {visLegs.length}</span>
                              </div>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                                <button type="button" style={{ ...btnS, borderColor: 'rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: 'var(--config-text-secondary, #ccc)', fontWeight: 700 }} onClick={(e) => { e.stopPropagation(); strategyActions.openStrategyViewModal(sp); }} title="View strategy configuration" aria-label={`View strategy configuration for ${sp.name}`}>View</button>
                                <button type="button" className={`${styles.actionButton} ${styles.strategyCloseButton}`} style={{ ...btnS, fontWeight: 700 }} onClick={(e) => { e.stopPropagation(); strategyActions.openStrategyCloseModal(sp); }} title="Close strategy row" aria-label={`Close strategy ${sp.name}`}>Close</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                        {isExp && visLegs.map((leg) => {
                          const sym = normalizeSymbol(leg.symbol) || leg.symbol || '--';
                          const normSym = normalizeSymbol(leg.symbol);
                          const liveTick = normSym ? ticksBySymbol[normSym] : undefined;
                          const legLp = resolveStrategyLegMarkPrice(leg, ticksBySymbol);
                          const legKey = `${sp.strategy_position_id}:${leg.leg_id}`;
                          const nqv = toFiniteNumber(leg.net_qty);
                          const nq = nqv != null ? nqv.toLocaleString() : '--';
                          let acb = toFiniteNumber(leg.avg_price);
                          if (acb == null && (nqv ?? 0) !== 0) {
                            const rem = basisRef.current[legKey];
                            if (typeof rem === 'number' && Number.isFinite(rem)) acb = rem;
                            else if (typeof legLp === 'number' && Number.isFinite(legLp)) { acb = legLp; basisRef.current[legKey] = legLp; }
                          }
                          const rawPnl = calculateStrategyLegPnl({ ...leg, avg_price: acb ?? leg.avg_price, net_qty: nqv ?? leg.net_qty }, ticksBySymbol);
                          const pnl = rawPnl ?? toFiniteNumber(leg.pnl);
                          const tradeAt = leg.trade_at ?? sp.created_at;
                          const updatedAt = liveTick?.ts ?? leg.updated_at ?? sp.updated_at;
                          return (
                            <tr key={`${sp.strategy_position_id}-${leg.leg_id}`} style={{ background: 'rgba(255,255,255,0.01)' }}>
                              <td style={tdS}>{leg.leg_id || '--'}</td>
                              <td style={tdS}>
                                <span style={symbolCellS}><SymbolLogo symbol={sym} size={16} /><span>{sym}</span></span>
                              </td>
                              <td style={tdS}>{nq}</td><td style={tdS}>{fmtPrice(acb)}</td><td style={tdS}>{fmtPrice(legLp)}</td>
                              <td style={{ ...tdS, ...pnlStyle(pnl) }}>{fmtPrice(pnl)}</td>
                              <td style={tdS}>{tradeAt ? fmtTime(tradeAt) : '--'}</td><td style={tdS}>{updatedAt ? fmtTime(updatedAt) : '--'}</td>
                              <td style={tdS}>{leg.action || '--'}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Strategy Close Confirmation Modal */}
      {strategyActions.strategyCloseTarget && (
        <StrategyCloseModal
          target={strategyActions.strategyCloseTarget}
          onDismiss={strategyActions.dismissStrategyCloseModal}
          onConfirm={strategyActions.handleConfirmStrategyClose}
          submitting={strategyActions.strategyCloseSubmitting}
          error={strategyActions.strategyCloseError}
        />
      )}

      {/* Strategy View Configuration Modal */}
      {strategyActions.strategyViewTarget && (
        <StrategyViewModal
          target={strategyActions.strategyViewTarget}
          config={strategyActions.strategyViewConfig}
          onClose={strategyActions.closeStrategyViewModal}
        />
      )}
    </div>
  );
};

export const PortfolioWidget: React.FC<WidgetInstanceProps> = (_props) => (
  <WidgetErrorBoundary name="Portfolio">
    <PortfolioInner />
  </WidgetErrorBoundary>
);

export default PortfolioWidget;
